import { useEffect, useRef } from 'react';

/**
 * Polling for QR-billing screens that already have a live WebSocket.
 *
 * Every QR screen (owner dashboard, chef, waiter, notifications) is driven by
 * socket pushes; the interval next to it is only a safety net for a dropped
 * socket. Those nets were what kept the Neon compute awake: Neon bills compute
 * by the second and only suspends after 5 minutes of silence, so any screen
 * ticking faster than that is a 24/7 billed database.
 *
 * `usePolling` pauses on a hidden tab, which fixes a phone left in a pocket but
 * NOT the actual situation here — a counter or kitchen tablet is left powered
 * on with the dashboard in the foreground, so `document.hidden` stays false all
 * night and the net keeps firing into an empty restaurant.
 *
 * So this hook gates on *activity* as well as visibility. A screen polls while
 * someone is using it or while the socket is delivering events; once both have
 * been quiet for `idleAfterMs` the poll stops entirely and the compute is free
 * to suspend. Nothing is lost by stopping: the socket stays connected and still
 * pushes, and the first touch (or socket event) fires the callback immediately,
 * so the screen is current before anyone can read it.
 *
 * @param {Function} callback    polled function; re-read every tick, so it does
 *                               not need memoising to avoid restarting the timer
 * @param {number}   intervalMs  tick spacing; falsy disables polling entirely
 * @param {object}   [options]
 * @param {boolean}  [options.enabled=true]      false suspends (e.g. no businessId yet)
 * @param {number}   [options.idleAfterMs]       quiet period before the poll stops
 */

const DEFAULT_IDLE_AFTER_MS = 10 * 60 * 1000;

let lastActivityAt = Date.now();
const wakeSubscribers = new Set();

/**
 * Mark the app as in use, restarting any poll that had gone idle.
 *
 * Called automatically on real user input, and manually from socket handlers —
 * an order arriving is activity even though nobody has touched the screen, and
 * the database is demonstrably awake at that moment anyway.
 */
export function markQrActivity() {
  lastActivityAt = Date.now();
  wakeSubscribers.forEach((notify) => {
    try {
      notify();
    } catch (err) {
      console.warn('⚠️ QR activity subscriber failed:', err);
    }
  });
}

if (typeof document !== 'undefined') {
  ['pointerdown', 'keydown', 'touchstart', 'wheel'].forEach((eventName) => {
    document.addEventListener(eventName, markQrActivity, { passive: true, capture: true });
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') markQrActivity();
  });
}

export function useBackupPolling(callback, intervalMs, options = {}) {
  const { enabled = true, idleAfterMs = DEFAULT_IDLE_AFTER_MS } = options;
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || !intervalMs) return undefined;

    // Mounting a screen means someone navigated to it.
    markQrActivity();

    let sleeping = false;

    const isAwake = () =>
      document.visibilityState === 'visible' && Date.now() - lastActivityAt < idleAfterMs;

    const timerId = setInterval(() => {
      if (!isAwake()) {
        sleeping = true;
        return;
      }
      savedCallback.current();
    }, intervalMs);

    // Catch up the moment the screen is used again rather than waiting out a
    // full tick on stale data.
    const onWake = () => {
      if (!sleeping) return;
      if (document.visibilityState !== 'visible') return;
      sleeping = false;
      savedCallback.current();
    };
    wakeSubscribers.add(onWake);

    return () => {
      clearInterval(timerId);
      wakeSubscribers.delete(onWake);
    };
  }, [intervalMs, enabled, idleAfterMs]);
}

export default useBackupPolling;
